import { FC, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { IconButton } from '@mui/material';

import { DeviationQuestionUserResponseDto } from 'src/generated/services/TFinancialApi';
import { formatDateString } from 'src/utils/date';
import { beautifyEnumWithTHC } from 'src/utils/utility';
import IconEdit from 'src/assets/images/icons/edit-2.svg';
import { ChargeDomain, LegType, useCalculateSubCharge } from 'src/use-cases/invoice/useQuestionnaire';
import { ChargeSelectionList } from 'src/presentations/components/molecules/charges/ChargeSelectionList';
import { DoubleChargeResolutionWrapper } from './components/DoubleChargeResolutionWrapper';
import { IFormResultsProps } from './types';
import { useSelectedDeviation } from './hooks/useSelectedDeviation';
import { useShipmentSelector } from '../../ShipmentSelector';

export const DeviationFormResults: FC<IFormResultsProps> = (props) => {
  const { title, answers, className, isEditable, legType, chargeDomain, moreActions, onClickEdit, theme, hideMoreActions = false, deviationId } = props;

  const { selectedDeviation } = useSelectedDeviation();
  const { shipment } = useShipmentSelector();

  // Check if this is NRCF: contractId === null OR shippingServiceId === null
  const isNrcf = !shipment?.contractId || !shipment?.shippingServiceId;

  // Hide more actions if NRCF or if explicitly set to hide
  const shouldHideMoreActions = hideMoreActions || isNrcf;

  // Check if deviation is auto-approved
  const isAutoApproved = selectedDeviation?.autoApproved === true;

  // Hide edit button if deviation is auto-approved
  const shouldShowEditButton = isEditable && !isAutoApproved;

  // Get subChargeDomains using the hook directly - skip for NRCF
  const { data: subChargeDomains } = useCalculateSubCharge(selectedDeviation?.id, legType as LegType, chargeDomain as ChargeDomain, isNrcf);

  const getAnswerValue = useCallback((answer: DeviationQuestionUserResponseDto) => {
    if (answer?.questionType === 'DATE_RANGE') {
      const dates = JSON.parse(answer?.answerText);
      if (dates?.start && dates?.end) {
        return (
          <div className='flex items-center max-w-[480px]'>
            <div className='w-1/2'>
              <h3 className='text-14 xl:text-16 mb-4'>Start:</h3>

              {formatDateString(dates.start)}
            </div>
            <div className='w-1/2'>
              <h3 className='text-14 xl:text-16 mb-4'>End:</h3>
              {formatDateString(dates.end)}
            </div>
          </div>
        );
      }
      return '-';
    }
    return answer?.answerText || answer?.answerName;
  }, []);

  const groupedSubChargeDomains = useMemo(
    () =>
      subChargeDomains?.reduce((acc, domain) => {
        if (!acc[domain.chargeType]) {
          acc[domain.chargeType] = [];
        }
        acc[domain.chargeType].push(domain);
        return acc;
      }, {}),
    [subChargeDomains]
  );

  // Helper function to render charge selection details - extracted to reduce nesting
  const renderChargeSelectionDetails = useCallback(
    (answer: DeviationQuestionUserResponseDto) => {
      if (!groupedSubChargeDomains) return null;

      // Find the selected charge from the answer
      const selectedChargeName = answer?.answerText || answer?.answerName;
      // Find which charge type group contains the selected charge
      const selectedChargeTypeEntry = Object.entries(groupedSubChargeDomains).find(([_, domains]) =>
        domains.some((domain) => domain.chargeName === selectedChargeName)
      );

      // Only display if we found the selected charge
      if (!selectedChargeTypeEntry) return null;

      const [chargeType, domains] = selectedChargeTypeEntry;
      const selectedCharge = domains.find((domain) => domain.chargeName === selectedChargeName);

      return (
        <div key={chargeType} className='relative'>
          <ChargeSelectionList
            charges={domains}
            selectedCharge={selectedCharge}
            mode='display'
            title={`Details of the charges based on ${chargeType}:`}
            showHeader
            maxHeight='max-h-600'
          />
        </div>
      );
    },
    [groupedSubChargeDomains]
  );

  if (answers?.length < 1) {
    return null;
  }

  return (
    <div className={clsx(theme === 'general' && 'mb-20 xl:mb-24 p-14 xl:p-16', className)}>
      <div className={clsx(theme === 'general' && 'container border-t border-neutral-20 pt-20 px-7')}>
        <div className='flex items-center mb-20'>
          <h3 className='text-14 xl:text-16 leading-18 xl:leading-20 font-bold'>{title}</h3>
          <div className='ml-auto flex items-center gap-12'>
            {!shouldHideMoreActions && <>{moreActions}</>}
            {shouldShowEditButton && (
              <IconButton onClick={onClickEdit}>
                <img src={IconEdit} alt='' className='w-20 xl:w-24' />
              </IconButton>
            )}
          </div>
        </div>

        {answers?.length > 0 ? (
          <div className='flex flex-col gap-12'>
            <div className='relative'>
              {answers.map((answer) => (
                <div
                  key={`answer-${answer?.answerOptionId}`}
                  className='mb-22 pb-16 border-b border-dashed border-neutral-20 last:mb-0 last:pb-0 last:border-0'
                >
                  <div className='mb-10'>
                    <span className='text-14 xl:text-16 leading-16 xl:leading-18 text-blue_grey-30'>{answer?.questionName}</span>
                  </div>
                  {/* Show answer text for non-charge-selection questions, or for charge selection if no table data */}
                  {answer?.questionDomainType !== 'SELECT_A_CHARGE_NAME' && <div className='text-14 xl:text-16 leading-16'>{getAnswerValue(answer)}</div>}

                  {/* Show charge name as text if table data is not available */}
                  {answer?.questionDomainType === 'SELECT_A_CHARGE_NAME' && !groupedSubChargeDomains && (
                    <div className='text-14 xl:text-16 leading-16'>{getAnswerValue(answer)}</div>
                  )}

                  {/* Display leg assignment and charge domain for specific question type */}
                  {answer?.questionDomainType === 'DO_YOU_ACCEPT_THIS_NEW_CHARGE_AS_IT' && (legType || chargeDomain) && (
                    <div className='mt-16 pt-22 border-dashed border-t border-neutral-20'>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-16'>
                        {legType && (
                          <div>
                            <div className='mb-10'>
                              <span className='text-14 xl:text-16 leading-16 xl:leading-18 text-blue_grey-30'>Leg Assignment</span>
                            </div>
                            <div className='text-14 xl:text-16 leading-16'>{beautifyEnumWithTHC(legType)}</div>
                          </div>
                        )}
                        {chargeDomain && (
                          <div>
                            <div className='mb-10'>
                              <span className='text-14 xl:text-16 leading-16 xl:leading-18 text-blue_grey-30'>Domain Assignment</span>
                            </div>
                            <div className='text-14 xl:text-16 leading-16'>{beautifyEnumWithTHC(chargeDomain)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {answer?.questionDomainType === 'SELECT_A_CHARGE_NAME' && renderChargeSelectionDetails(answer)}

                  {answer?.questionDomainType === 'DO_YOU_ACCEPT_THIS_CHARGE_AS_IS' && answer?.answerType === 'RESOLVE_ALL' && deviationId && (
                    <div className='mt-16 pt-12 border-dashed border-t border-neutral-20'>
                      <DoubleChargeResolutionWrapper deviationId={deviationId} decisions={{}} onDecisionChange={() => {}} disabled />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p>No Data</p>
        )}
      </div>
    </div>
  );
};
